import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { parseCreditNote } from "../../../services/invoiceService";
import {
  invoicesToTick,
  parseSummary,
  parseWarnings,
  parsedAckFields,
  parsedFormValues,
} from "../utils/creditNoteParse";

const FORM_FIELDS = ["cnNumber", "cnValue", "cnDate"];

// Owns the parse of an uploaded credit note for the submission form (ENG-29736):
// runs it, applies the result to fields the vendor has not edited, and derives the
// warnings, field status, summary and extra ack fields. Only the latest parse call's
// result is ever applied — a slow parse of a replaced file is dropped, even when a
// Re-Upload kept the same attachment id.
//
// `values` are the form's current values; `setters` write cnNumber, cnValue, cnDate
// and the invoice selection (`invoices`, a Set of FINS invoice_numbers).
export function useCreditNoteParse({ poCode, invoices, expectedAmount, values, setters, onFailure }) {
  const [parseResult, setParseResult] = useState(null);
  const editedRef = useRef(new Set());
  const latestCallRef = useRef(0);
  const mutation = useMutation({
    mutationFn: (id) => parseCreditNote({ attachmentId: id, poCode }),
  });

  const apply = (result) => {
    setParseResult(result || null);
    const parsedValues = parsedFormValues(result?.parsed);
    const edited = editedRef.current;
    FORM_FIELDS.forEach((field) => {
      if (parsedValues[field] && !edited.has(field)) setters[field](parsedValues[field]);
    });
    const ticks = invoicesToTick(result?.matched_invoices, invoices);
    if (ticks.size > 0 && !edited.has("invoices")) setters.invoices(ticks);
  };

  const parse = (attachmentId) => {
    // A token per call, not the id: Re-Upload usually keeps the attachment id.
    const call = ++latestCallRef.current;
    setParseResult(null);
    mutation.mutate(attachmentId, {
      onSuccess: (result) => {
        if (latestCallRef.current === call) apply(result);
      },
      onError: (err) => {
        // 503 is the feature switched off: manual entry, no notice.
        if (latestCallRef.current !== call || err?.response?.status === 503) return;
        onFailure?.(err);
      },
    });
  };

  // A field the vendor changed by hand is never overwritten by a later parse.
  const noteEdited = (field) => editedRef.current.add(field);
  const markEdited = (field, setter) => (value) => {
    noteEdited(field);
    setter(value);
  };

  const parsedValues = parsedFormValues(parseResult?.parsed);
  const allWarnings = parseWarnings(parseResult, { poCode, expectedAmount });
  // The value warning describes the parsed total; it goes once the vendor changes the field.
  const warnings = {
    ...allWarnings,
    value: values.cnValue === parsedValues.cnValue ? allWarnings.value : null,
  };
  const fieldStatus = (field, warning) => {
    if (!parsedValues[field]) return "none";
    if (warning) return "invalid";
    return values[field] === parsedValues[field] ? "valid" : "none";
  };
  // A document for another PO still sends its date, never its printed metadata.
  const ackFields = (cnDate) =>
    parsedAckFields(parseResult?.po_number_matches === false ? null : parseResult?.parsed, cnDate);

  return {
    parse,
    parsing: mutation.isLoading,
    parseResult,
    noteEdited,
    markEdited,
    warnings,
    fieldStatus,
    summary: parseSummary(parseResult?.parsed),
    ackFields,
  };
}
